import { Fragment, useEffect, useState, type ReactNode } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { isEqual as _isEqual, compact, isNil } from "lodash";
import { Link } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import {
  defaultOrderPageSorting,
  useAttributedAtom,
  useCustomerIdAtom,
  useCustomerTagsAtom,
  useDiscountCodesAtom,
  useDiscountValueAtom,
  useNumberOfOrderItemsAtom,
  useNumberOfTouchpointsAtom,
  useOrderIdAtom,
  useOrderNumberAtom,
  useOrderPageSortingAtom,
  useOrderTagsAtom,
  useOrderTypesAtom,
  useOrderValueAtom,
  useProductsPurchasedAtom,
  useRefundValueAtom,
  useShippingValueAtom,
  useSourceNameAtom,
  useSubscriptionsAtom,
  useTaxValueAtom,
  type ValueComparisonInput,
} from "@/atoms/order-page-atoms";
import { usePageTitle } from "@/atoms/page-title-atom";
import { useUser } from "@components/user-context";
import { LogOnMount } from "@components/v2/analytics";
import { AnnotationsFlyout } from "@features/custom-annotations";
import { useAnnotations } from "@features/custom-annotations/hooks/use-annotations";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { useNorthbeamMutation } from "@hooks/http";
import { useOrderQueriesLoading } from "@hooks/use-order-page-loading";
import { prettyLogEvent } from "@internal/analytics";
import {
  type ExportedOrderData,
  type ExportedOrderDataVariables,
  type ValueFloatComparisonInput,
  type ValueIntComparisonInput,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import {
  useOrdersPageFilters,
  useOrdersQueryParams,
} from "@pages/orders/use-orders-filters";
import { EXPORT_ORDER_DATA } from "@pages/settings/queries";
import { Badge } from "@shared/badge";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { TextInput } from "@shared/text-input";

import { OrdersChart } from "./orders-chart";
import { OrdersControl } from "./orders-control";
import { OrdersTable } from "./orders-table/orders-table";

function isEqual<T>(a: T | null | undefined, b: T | null | undefined) {
  if (isNil(a) && isNil(b)) {
    return true;
  }
  return _isEqual(a, b);
}

function getOrderValue(orderValue: string | null | undefined) {
  return isNil(orderValue) || orderValue.trim() === ""
    ? undefined
    : Number(orderValue);
}

function comparisonInputIsValid<
  T extends ValueIntComparisonInput | ValueFloatComparisonInput,
>(input: Partial<T> | null | undefined): input is T | null {
  if (isNil(input?.comparisonOperator) && !isNil(input?.value)) {
    return false;
  }
  if (!isNil(input?.comparisonOperator) && isNil(input?.value)) {
    return false;
  }
  return true;
}

const useQueryStateEqualsInternalQueryState = () => {
  const [orderTags] = useOrderTagsAtom();
  const [sourceName] = useSourceNameAtom();
  const [customerTags] = useCustomerTagsAtom();
  const [subscriptions] = useSubscriptionsAtom();
  const [orderTypes] = useOrderTypesAtom();
  const [products] = useProductsPurchasedAtom();
  const [discountCodes] = useDiscountCodesAtom();
  const [orderId] = useOrderIdAtom();
  const [orderNumber] = useOrderNumberAtom();
  const [customerId] = useCustomerIdAtom();
  const [numberOfOriginalItems] = useNumberOfOrderItemsAtom();
  const [numberOfTouchpoints] = useNumberOfTouchpointsAtom();
  const [attributed] = useAttributedAtom();
  const [orderValue] = useOrderValueAtom();
  const [discountValue] = useDiscountValueAtom();
  const [shippingValue] = useShippingValueAtom();
  const [taxValue] = useTaxValueAtom();
  const [refundValue] = useRefundValueAtom();

  const [state] = useOrdersQueryParams();

  if (!isEqual(state.attributed, attributed)) {
    return false;
  }
  if (!isEqual(state.orderTags, orderTags)) {
    return false;
  }
  if (!isEqual(state.sourceName, sourceName)) {
    return false;
  }
  if (!isEqual(state.customerTags, customerTags)) {
    return false;
  }
  if (!isEqual(state.subscriptions, subscriptions)) {
    return false;
  }
  if (!isEqual(state.discountCodes, discountCodes)) {
    return false;
  }
  if (!isEqual(state.orderTypes, orderTypes)) {
    return false;
  }
  if (!isEqual(state.products, products)) {
    return false;
  }
  if (!isEqual(state.orderId, orderId)) {
    return false;
  }
  if (!isEqual(state.orderNumber, orderNumber)) {
    return false;
  }
  if (!isEqual(state.customerId, customerId)) {
    return false;
  }
  if (!isEqual(state.numberOfOriginalItems, numberOfOriginalItems)) {
    return false;
  }
  if (!isEqual(state.numberOfTouchpoints, numberOfTouchpoints)) {
    return false;
  }

  if (
    !isEqual(
      state.orderValue?.comparisonOperator,
      orderValue?.comparisonOperator,
    ) ||
    !isEqual(state.orderValue?.value, getOrderValue(orderValue?.value))
  ) {
    return false;
  }

  if (
    !isEqual(
      state.discountValue?.comparisonOperator,
      discountValue?.comparisonOperator,
    ) ||
    !isEqual(state.discountValue?.value, getOrderValue(discountValue?.value))
  ) {
    return false;
  }

  if (
    !isEqual(
      state.shippingValue?.comparisonOperator,
      shippingValue?.comparisonOperator,
    ) ||
    !isEqual(state.shippingValue?.value, getOrderValue(shippingValue?.value))
  ) {
    return false;
  }

  if (
    !isEqual(
      state.taxValue?.comparisonOperator,
      taxValue?.comparisonOperator,
    ) ||
    !isEqual(state.taxValue?.value, getOrderValue(taxValue?.value))
  ) {
    return false;
  }

  if (
    !isEqual(
      state.refundValue?.comparisonOperator,
      refundValue?.comparisonOperator,
    ) ||
    !isEqual(state.refundValue?.value, getOrderValue(refundValue?.value))
  ) {
    return false;
  }

  return true;
};

function ApplyFiltersButton() {
  const { addToast } = useToasts();
  const [orderTags, setOrderTags] = useOrderTagsAtom();
  const [sourceName, setSourceName] = useSourceNameAtom();
  const [customerTags, setCustomerTags] = useCustomerTagsAtom();
  const [subscriptions, setSubscriptions] = useSubscriptionsAtom();
  const [orderTypes, setOrderTypes] = useOrderTypesAtom();
  const [products, setProducts] = useProductsPurchasedAtom();
  const [discountCodes, setDiscountCodes] = useDiscountCodesAtom();
  const [orderId, setOrderId] = useOrderIdAtom();
  const [orderNumber, setOrderNumber] = useOrderNumberAtom();
  const [customerId, setCustomerId] = useCustomerIdAtom();
  const [numberOfOriginalItems, setNumberOfOriginalItems] =
    useNumberOfOrderItemsAtom();
  const [numberOfTouchpoints, setNumberOfTouchpoints] =
    useNumberOfTouchpointsAtom();
  const [attributed, setAttributed] = useAttributedAtom();
  const [orderValue, setOrderValue] = useOrderValueAtom();
  const [discountValue, setDiscountValue] = useDiscountValueAtom();
  const [shippingValue, setShippingValue] = useShippingValueAtom();
  const [taxValue, setTaxValue] = useTaxValueAtom();
  const [refundValue, setRefundValue] = useRefundValueAtom();
  const [, setSorting] = useOrderPageSortingAtom();
  const { user } = useUser();
  const [state, setQuery] = useOrdersQueryParams();
  const isQueryStateClean = useQueryStateEqualsInternalQueryState();
  const orderQueriesLoading = useOrderQueriesLoading();

  useEffect(() => {
    if (state.subscriptions) {
      setSubscriptions(compact(state.subscriptions));
    }
    if (state.orderTags) {
      setOrderTags(compact(state.orderTags));
    }
    if (state.sourceName) {
      setSourceName(compact(state.sourceName));
    }
    if (state.customerTags) {
      setCustomerTags(compact(state.customerTags));
    }
    if (state.orderTypes) {
      setOrderTypes(compact(state.orderTypes));
    }
    if (state.products) {
      setProducts(compact(state.products));
    }
    if (state.attributed) {
      setAttributed(compact(state.attributed));
    }
    if (state.orderId) {
      setOrderId(state.orderId);
    }
    if (state.orderNumber) {
      setOrderNumber(state.orderNumber);
    }
    if (state.customerId) {
      setCustomerId(state.customerId);
    }
    if (state.discountCodes) {
      setDiscountCodes(compact(state.discountCodes));
    }
    if (!isNil(state.numberOfOriginalItems)) {
      setNumberOfOriginalItems({
        value: Number(state.numberOfOriginalItems.value),
        comparisonOperator: state.numberOfOriginalItems.comparisonOperator,
      });
    }
    if (!isNil(state.numberOfTouchpoints)) {
      setNumberOfTouchpoints({
        value: Number(state.numberOfTouchpoints.value),
        comparisonOperator: state.numberOfTouchpoints.comparisonOperator,
      });
    }

    if (state.orderValue) {
      setOrderValue({
        value: String(state.orderValue.value),
        comparisonOperator: state.orderValue.comparisonOperator,
      });
    }

    if (state.discountValue) {
      setDiscountValue({
        value: String(state.discountValue.value),
        comparisonOperator: state.discountValue.comparisonOperator,
      });
    }

    if (state.shippingValue) {
      setShippingValue({
        value: String(state.shippingValue.value),
        comparisonOperator: state.shippingValue.comparisonOperator,
      });
    }

    if (state.shippingValue) {
      setTaxValue({
        value: String(state.shippingValue.value),
        comparisonOperator: state.shippingValue.comparisonOperator,
      });
    }

    if (state.refundValue) {
      setRefundValue({
        value: String(state.refundValue.value),
        comparisonOperator: state.refundValue.comparisonOperator,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setSubscriptions,
    setOrderTags,
    setSourceName,
    setCustomerTags,
    setOrderTypes,
    setProducts,
    setDiscountCodes,
    setAttributed,
    setOrderId,
    setOrderNumber,
    setCustomerId,
    setNumberOfOriginalItems,
    setNumberOfTouchpoints,
    setOrderValue,
    setDiscountValue,
    setShippingValue,
    setTaxValue,
    setRefundValue,
  ]);

  return (
    <div className="flex">
      <div className="flex relative">
        {!isQueryStateClean && (
          <Badge
            badgeColor="#f08484"
            className="absolute right-0 top-1"
            size="md"
          />
        )}
        <SecondaryButton
          className="py-2 px-3 my-2"
          disabled={orderQueriesLoading}
          onClick={() => {
            const validateValue = (
              value: ValueComparisonInput | null,
              inputName: string,
            ) => {
              const verifiedValue = getOrderValue(value?.value);

              if (!isNil(verifiedValue) && isNaN(verifiedValue)) {
                addToast(`Sorry! Your ${inputName} is not a number`, {
                  appearance: "error",
                });
                return false;
              }

              const potentialValue =
                !verifiedValue && !value?.comparisonOperator
                  ? null
                  : {
                      value: verifiedValue,
                      comparisonOperator: value?.comparisonOperator,
                    };

              if (!comparisonInputIsValid(potentialValue)) {
                addToast(
                  `Sorry! ${inputName} must have both a value and comparison operator`,
                  {
                    appearance: "error",
                  },
                );
                return false;
              }

              return potentialValue;
            };

            const potentialOrderValue = validateValue(
              orderValue,
              "Order value",
            );

            if (potentialOrderValue === false) {
              return;
            }

            const potentialDiscountValue = validateValue(
              discountValue,
              "Discount value",
            );

            if (potentialDiscountValue === false) {
              return;
            }

            const potentialShippingValue = validateValue(
              shippingValue,
              "Shipping value",
            );

            if (potentialShippingValue === false) {
              return;
            }

            const potentialTaxValue = validateValue(taxValue, "Tax value");

            if (potentialTaxValue === false) {
              return;
            }

            const potentialRefundValue = validateValue(
              refundValue,
              "Refund value",
            );

            if (potentialRefundValue === false) {
              return;
            }

            if (!comparisonInputIsValid(numberOfOriginalItems)) {
              addToast(
                "Sorry! Number of Original Items must have both a value and comparison operator",
                {
                  appearance: "error",
                },
              );
              return;
            }

            if (!comparisonInputIsValid(numberOfTouchpoints)) {
              addToast(
                "Sorry! Number of Touchpoints must have both a value and comparison operator",
                {
                  appearance: "error",
                },
              );
              return;
            }

            const query: Parameters<typeof setQuery>[0] = {
              discountCodes,
              customerTags,
              orderTags,
              sourceName,
              subscriptions,
              orderTypes,
              products,
              attributed,
              orderId,
              orderNumber,
              customerId,
              numberOfOriginalItems,
              numberOfTouchpoints,
              orderValue: potentialOrderValue,
              discountValue: potentialDiscountValue,
              shippingValue: potentialShippingValue,
              taxValue: potentialTaxValue,
              refundValue: potentialRefundValue,
            };

            prettyLogEvent.applyFilters({
              page: Pages.ORDERS,
              data: query,
              user,
            });
            return setQuery(query);
          }}
        >
          APPLY FILTERS AND SEARCH
        </SecondaryButton>
      </div>
      <SecondaryButton
        className="ml-2 py-2 px-3 my-2"
        disabled={orderQueriesLoading}
        onClick={() => {
          prettyLogEvent.clearFilters({
            page: Pages.ORDERS,
            user,
          });
          setQuery({
            discountCodes: undefined,
            subscriptions: undefined,
            orderTags: undefined,
            sourceName: undefined,
            customerTags: undefined,
            orderTypes: undefined,
            products: undefined,
            attributed: undefined,
            orderId: undefined,
            orderNumber: undefined,
            customerId: undefined,
            numberOfOriginalItems: undefined,
            numberOfTouchpoints: undefined,
            orderValue: undefined,
            discountValue: undefined,
            shippingValue: undefined,
            taxValue: undefined,
            refundValue: undefined,
          });
          setDiscountCodes(null);
          setOrderTags(null);
          setSourceName(null);
          setCustomerTags(null);
          setSubscriptions(null);
          setOrderTypes(null);
          setProducts(null);
          setOrderId(null);
          setCustomerId(null);
          setNumberOfOriginalItems(null);
          setNumberOfTouchpoints(null);
          setAttributed(null);
          setOrderValue(null);
          setDiscountValue(null);
          setShippingValue(null);
          setTaxValue(null);
          setRefundValue(null);
          setSorting(defaultOrderPageSorting);
          setOrderNumber(null);
        }}
      >
        CLEAR ALL
      </SecondaryButton>
    </div>
  );
}

export function ExportData({ className }: { className?: string }) {
  const filterOptions = useOrdersPageFilters();

  const { user } = useUser();
  const { addToast } = useToasts();
  const [exportFormat, setExportFormat] = useState<"default" | "api" | null>(
    null,
  );
  const [fileName, setFileName] = useState("order_data");
  const [exportData, { loading }] = useNorthbeamMutation<
    ExportedOrderData,
    ExportedOrderDataVariables
  >(EXPORT_ORDER_DATA, { useCpuIntensive: true });

  useEffect(() => {
    setFileName(exportFormat === "api" ? "orders_api" : "order_data");
  }, [exportFormat]);

  return (
    <>
      <Modal
        isOpen={exportFormat !== null}
        onClose={() => setExportFormat(null)}
        titleContent={
          <div className="text-lg font-semibold">
            <i
              className="fa-regular fa-sharp fa-download mr-5"
              style={{ fontSize: "1.5em" }}
            />
            Export your Order Data
          </div>
        }
      >
        <div className="mt-4">
          Your order data export will be based on the filters that you have
          applied on the order page.
        </div>
        {exportFormat === "api" && (
          <div className="mt-4">
            Your export will be in the format of the Orders API CSV template.
          </div>
        )}
        <div className="mt-4">
          Your export may take up to 1 hour to complete depending on the size of
          the export.
        </div>
        <div className="mt-4">
          Exports can be found in the{" "}
          <Link to="../customers/documents" target="_blank" rel="noreferrer">
            Documents page.
          </Link>
        </div>
        <div className="mt-4">
          File name:
          <TextInput
            className="text-sm"
            value={fileName}
            onChange={(e) => {
              if (e.target.value.match(/^[A-Za-z0-9_\-]*$/)) {
                setFileName(e.target.value);
              }
            }}
          />
          {!fileName && (
            <div className="font-normal text-[0.65rem] text-mars mt-0.5">
              File Name is required
            </div>
          )}
          {fileName && fileName.length < 3 && (
            <div className="font-normal text-[0.65rem] text-mars mt-0.5">
              File Name must be at least 3 characters long
            </div>
          )}
        </div>
        <div className="text-right">
          <PrimaryButton
            className="mt-8"
            isDisabled={loading || !fileName || fileName.length < 3}
            loading={loading}
            size="md"
            onClick={async () => {
              if (!exportFormat) {
                return;
              }

              const name = `${fileName}-${new Date().getTime()}`;
              await exportData({
                variables: {
                  filterOptions,
                  name,
                  format: exportFormat,
                },
              });

              addToast(
                <div>
                  <div>Your order export has started!</div>
                  <div className="mt-3">
                    When your export is done it will live in the Documents page
                    under the name:
                  </div>
                  <div className="mt-3">{name}.csv</div>
                </div>,
                { appearance: "success", autoDismiss: false },
              );
              prettyLogEvent.onSubmit({
                page: Pages.ORDERS,
                additionalActionInfo: "order-export",
                data: { filterOptions, name, exportFormat },
                user,
              });
              setExportFormat(null);
            }}
          >
            EXPORT
          </PrimaryButton>
        </div>
      </Modal>
      {user.featureFlags.enableOrdersAdvancedExport ? (
        <div className="relative">
          <Menu>
            {({ open }) => (
              <>
                <MenuButton
                  as="button"
                  className={clsx("button secondary-button sm", {
                    "text-white bg-cosmic border-cosmic": open,
                  })}
                >
                  <span className="mr-2">EXPORT TABLE DATA</span>
                  <motion.i
                    initial={false}
                    animate={{ rotate: open ? 0 : 180 }}
                    transition={{ duration: 0.2 }}
                    className={clsx("fa-regular fa-sharp fa-chevron-up", {
                      "text-white": open,
                    })}
                  />
                </MenuButton>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <MenuItems
                    className={clsx(
                      "absolute z-10 right-0 bg-white rounded-lg elevation-1",
                      "text-sm pt-1 pb-2 w-[180px]",
                    )}
                  >
                    <ExportMenuOption
                      onClick={() => setExportFormat("default")}
                    >
                      <i className="fa-regular fa-sharp fa-table text-primary mr-2" />
                      Default format
                    </ExportMenuOption>
                    <ExportMenuOption onClick={() => setExportFormat("api")}>
                      <div className="relative">
                        <i className="fa-regular fa-sharp fa-table text-primary mr-2" />
                        <span
                          className={clsx(
                            "absolute left-[4px] bottom-0 bg-cosmic text-white rounded",
                            "text-[7px] leading-[7px] py-[1px] px-[4px]",
                          )}
                        >
                          API
                        </span>
                      </div>
                      Orders API format
                    </ExportMenuOption>
                  </MenuItems>
                </Transition>
              </>
            )}
          </Menu>
        </div>
      ) : (
        <SecondaryButton
          className={className}
          isDisabled={loading}
          onClick={async () => {
            setExportFormat("default");
          }}
        >
          EXPORT DATA
        </SecondaryButton>
      )}
    </>
  );
}

const ExportMenuOption = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick(): void;
}) => (
  <MenuItem as={Fragment}>
    {({ focus }) => {
      return (
        <div
          className={clsx(
            "w-full text-left px-4 h-8 flex items-center cursor-pointer",
            {
              "bg-cosmic-4": focus,
            },
          )}
          onClick={onClick}
        >
          {children}
        </div>
      );
    }}
  </MenuItem>
);

function Orders(): JSX.Element {
  const [, setPageTitle] = usePageTitle();
  const { user } = useUser();

  useEffect(() => {
    setPageTitle("Orders");
  }, [setPageTitle]);

  useEffect(() => {
    prettyLogEvent.visited({
      page: Pages.ORDERS,
      user,
    });
  }, [user]);

  const {
    annotations,
    fetchAnnotations,
    isAnnotationsLoading,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
  } = useAnnotations();

  return (
    <>
      <LogOnMount name="Orders page" />
      <AnnotationsFlyout
        annotations={annotations}
        fetchAnnotations={fetchAnnotations}
        isAnnotationsLoading={isAnnotationsLoading}
        createAnnotation={createAnnotation}
        updateAnnotation={updateAnnotation}
        deleteAnnotation={deleteAnnotation}
      />
      <OrdersControl isLoading={false} />
      <div className="mx-4">
        <OrdersChart />
        <div className="flex justify-between items-center">
          <ApplyFiltersButton />
          <ExportData />
        </div>
        <OrdersTable />
      </div>
    </>
  );
}

export default Orders;
